import Image from 'next/image';
import {type FC, useState} from 'react';
import {
  FaExternalLinkAlt,
  FaInstagram,
  FaTiktok,
  FaYoutube,
} from 'react-icons/fa';
import {FaChevronRight, FaXTwitter} from 'react-icons/fa6';
import APP_STORE_ANDROID_IMAGE from '../../../../../public/next-image/common/appStore/android.png';
import APP_STORE_IOS_IMAGE from '../../../../../public/next-image/common/appStore/ios.png';
import LOGO_SQUARE_WHITE from '../../../../../public/next-image/common/logo/square_white.svg';
import {
  GSSKT_INSTAGRAM_URL,
  GSSKT_TIKTOK_URL,
  GSSKT_TWITTER_URL,
  GSSKT_YOUTUBE_URL,
} from '../../../../config/constants/hosts';
import {CommonLink} from '../../CommonLink';
import {
  footerMenuItems,
  footerMenuItems2,
  gssktAppLink,
  searchCompaniesFromIndustryItems,
  searchReportsFromDepartmentItems,
  searchReportsFromIndustryItems,
} from '../items';

type Props = {
  className?: string;
};

type AccordionItemProps = {
  title: string;
  isOpen: boolean;
  onClick: () => void;
  items: Array<{href: string; label: string}>;
};

const AccordionItem: FC<AccordionItemProps> = ({
  title,
  isOpen,
  onClick,
  items,
}) => (
  <li className="px-8 mb-12">
    <div className="flex justify-between items-center" onClick={onClick}>
      <span>{title}</span>
      <FaChevronRight
        size={16}
        className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
      />
    </div>
    <div
      className={`overflow-hidden ${
        isOpen
          ? 'transition-max-height duration-1000 ease-in-out max-h-screen'
          : 'transition-max-height duration-300 ease-in-out max-h-0'
      }`}
    >
      <ul className="ml-8">
        {items.map((item) => (
          <li key={item.label} className="my-8">
            <a href={item.href} className="block">
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </li>
);

export const MobileFooter: FC<Props> = ({className}) => {
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [isIndustryOpen, setIsIndustryOpen] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const handleCompanyClick = () => {
    setIsCompanyOpen(!isCompanyOpen);
  };

  const handleIndustryClick = () => {
    setIsIndustryOpen(!isIndustryOpen);
  };

  const handleDepartmentClick = () => {
    setDepartmentOpen(!departmentOpen);
  };

  return (
    <div className={className}>
      <div className="flex flex-col bg-background-footer px-16 pt-24 pb-[100px] text-white">
        <div className="flex flex-col text-base pb-24">
          <ul>
            {footerMenuItems.map((item) => (
              <li key={item.label} className="px-8 mb-12">
                <CommonLink
                  href={item.href}
                  className="block"
                  target={item.target}
                >
                  {item.label}
                  {item.target && (
                    <span className="inline-flex">
                      <FaExternalLinkAlt />
                    </span>
                  )}
                </CommonLink>
              </li>
            ))}
            <AccordionItem
              title="企業を探す"
              isOpen={isCompanyOpen}
              onClick={handleCompanyClick}
              items={searchCompaniesFromIndustryItems}
            />
            <AccordionItem
              title="業界のES・体験記"
              isOpen={isIndustryOpen}
              onClick={handleIndustryClick}
              items={searchReportsFromIndustryItems}
            />
            <AccordionItem
              title="部門・職種別のES・体験記"
              isOpen={departmentOpen}
              onClick={handleDepartmentClick}
              items={searchReportsFromDepartmentItems}
            />
            {footerMenuItems2.map((item) => {
              const {label, ...itemProps} = item;

              return (
                <li key={item.label} className="px-8 mb-12">
                  <CommonLink {...itemProps}>{label}</CommonLink>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex flex-col items-start justify-between">
          <div className="flex justify-center items-center gap-12">
            <div className="pb-24">
              <Image
                src={LOGO_SQUARE_WHITE}
                alt="外資就活"
                className="w-full h-[60px]"
              />
            </div>
            <div className="flex justify-between gap-16">
              <CommonLink
                href={GSSKT_TWITTER_URL}
                target="_blank"
                rel="noreferrer"
              >
                <FaXTwitter size={24} />
              </CommonLink>
              <CommonLink
                href={GSSKT_INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
              >
                <FaInstagram size={24} />
              </CommonLink>
              <CommonLink
                href={GSSKT_YOUTUBE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <FaYoutube size={24} />
              </CommonLink>
              <CommonLink
                href={GSSKT_TIKTOK_URL}
                target="_blank"
                rel="noreferrer"
              >
                <FaTiktok size={24} />
              </CommonLink>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex max-w-[260px] gap-8 pb-12">
              <CommonLink href={gssktAppLink.ios}>
                <Image
                  src={APP_STORE_IOS_IMAGE}
                  alt="Download on the App Store"
                />
              </CommonLink>
              <CommonLink href={gssktAppLink.android}>
                <Image
                  src={APP_STORE_ANDROID_IMAGE}
                  alt="GET IT ON Google Play"
                />
              </CommonLink>
            </div>
            <div className="text-sm text-white">
              © Howtelevision,Inc. All Rights Reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
